import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, ArrowLeft, Mail, Phone, Trash2, Check, X as XIcon, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import AiLeadInsights from '../components/AiLeadInsights';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger
} from '../components/ui/alert-dialog';

const statusStyles = {
  pending: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  confirmed: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  cancelled: 'bg-red-500/10 text-red-300 border-red-500/30',
  completed: 'bg-white/5 text-slate-300 border-white/15',
};

const AdminConsultationsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/consultations');
      setItems(data);
    } catch {
      toast({ title: 'Failed to load consultations' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => { await logout(); navigate('/admin/login'); };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/consultations/${id}`, { status });
      toast({ title: `Marked as ${status}` });
      load();
    } catch (e) {
      toast({ title: 'Update failed' });
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/consultations/${id}`);
      toast({ title: 'Deleted' });
      load();
    } catch (e) {
      toast({ title: 'Delete failed' });
    }
  };

  const filtered = filter === 'all' ? items : items.filter((i) => i.status === filter);

  const counts = items.reduce((acc, i) => {
    acc[i.status] = (acc[i.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#05080f]">
      <header className="border-b border-white/5 bg-[#05080f]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1.5">
              <ArrowLeft size={14} /> Dashboard
            </Link>
            <div className="h-4 w-px bg-white/10"></div>
            <h1 className="text-white font-display text-lg">Consultations</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <div className="text-white text-sm">{user?.username}</div>
              <div className="text-slate-500 text-xs">{user?.email}</div>
            </div>
            <Button onClick={handleLogout} variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10">
              <LogOut size={14} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight">Consultation requests</h2>
            <p className="text-slate-400 mt-1 text-sm">Meeting requests submitted via the AI Consulting calendar.</p>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap mb-6">
          {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`text-xs px-3.5 py-1.5 rounded-full border transition-colors capitalize
                ${filter === f
                  ? 'bg-amber-500/15 text-amber-200 border-amber-500/40'
                  : 'bg-white/[0.03] text-slate-300 border-white/10 hover:border-amber-500/30'}`}>
              {f} {f !== 'all' && counts[f] ? `(${counts[f]})` : ''}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-slate-400 py-16 text-center">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="card-dark rounded-3xl p-12 text-center">
            <p className="text-slate-300">No consultation requests yet.</p>
            <p className="text-slate-500 text-sm mt-2">Requests will appear here as they come in through the AI Consulting calendar on your site.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filtered.map((c) => (
              <div key={c.id} className="card-dark rounded-2xl p-5 md:p-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-white font-semibold text-lg">{c.name}</h3>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border capitalize ${statusStyles[c.status]}`}>{c.status}</span>
                    </div>
                    <div className="mt-1 text-amber-300 text-sm font-medium inline-flex items-center gap-2">
                      <Clock size={13} />
                      {new Date(c.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} • {c.time_slot}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-4 text-sm">
                      <a href={`mailto:${c.email}`} className="text-slate-300 hover:text-amber-300 inline-flex items-center gap-1.5">
                        <Mail size={13} /> {c.email}
                      </a>
                      {c.phone && (
                        <a href={`tel:${c.phone}`} className="text-slate-300 hover:text-amber-300 inline-flex items-center gap-1.5">
                          <Phone size={13} /> {c.phone}
                        </a>
                      )}
                    </div>
                    {c.notes && (
                      <div className="mt-3 text-slate-400 text-sm leading-relaxed border-l-2 border-white/10 pl-3">
                        {c.notes}
                      </div>
                    )}
                    <div className="mt-3 text-xs text-slate-500">
                      Received {new Date(c.created_at).toLocaleString()}
                    </div>

                    <div className="mt-4">
                      <AiLeadInsights
                        kind="consultation"
                        payload={{
                          name: c.name, email: c.email, phone: c.phone,
                          date: c.date, time_slot: c.time_slot,
                          purpose: c.purpose, notes: c.notes
                        }}
                        testIdPrefix={`consult-${c.id}`}
                      />
                    </div>
                  </div>

                  <div className="flex md:flex-col gap-2 md:min-w-[160px]">
                    {c.status === 'pending' && (
                      <>
                        <Button size="sm" onClick={() => updateStatus(c.id, 'confirmed')}
                          className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/30 h-9">
                          <Check size={14} className="mr-1.5" /> Confirm
                        </Button>
                        <Button size="sm" onClick={() => updateStatus(c.id, 'cancelled')} variant="outline"
                          className="border-white/10 bg-white/5 text-white hover:bg-white/10 h-9">
                          <XIcon size={14} className="mr-1.5" /> Cancel
                        </Button>
                      </>
                    )}
                    {c.status === 'confirmed' && (
                      <Button size="sm" onClick={() => updateStatus(c.id, 'completed')} variant="outline"
                        className="border-white/10 bg-white/5 text-white hover:bg-white/10 h-9">
                        Mark completed
                      </Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20 h-9">
                          <Trash2 size={14} />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#0b1220] border-white/10 text-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this request?</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(c.id)} className="bg-red-500/80 hover:bg-red-500 text-white">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminConsultationsPage;
